import { useCallback, useEffect, useRef, useState } from 'react';
import { datasetsApi } from '../lib/api';
import { jobsApi, trackJob } from '../lib/jobs-api';
import {
  hasProcessedRaster, initialRasterStyle, isRasterDataset, rasterApi, rasterBounds, rasterJobState,
  rasterTileUrl, startTileAuth,
} from '../lib/raster-api';
import { isDem, terrainApi } from '../lib/terrain-api';
import { datasetIdsFromParams, extraLayerIds } from '../lib/map-url';

/**
 * Raster dataset layers on the map (docs/RASTER_SUPPORT.md). `/map?dataset=<id>` for a
 * raster dataset adds a MapLibre `raster` source fed by the tile endpoint; tile requests
 * carry the bearer token through SurveyMap's transformRequest. Changing colormap/range
 * swaps the tile URL (a new source revision); opacity is a paint property. With the
 * probe on, a map click reads the pixel value from /raster/value.
 *
 * status: 'idle' (no raster in the URL) | 'loading' | 'processing' | 'ready' | 'error'
 */

// ── Pure helpers (unit-tested) ──────────────────────────────────────────────

const DEM_TILE_PRODUCTS = ['elevation', 'hillshade', 'slope', 'aspect'];

/** The layer entry for a processed raster dataset. For a DEM (docs/DEM_TERRAIN.md)
 *  `overrides.dem` picks the product (elevation | hillshade | slope | aspect | contours;
 *  contours draw over the elevation). */
export function buildRasterLayer(dataset, overrides = {}) {
  const raster = dataset?.metadata?.raster;
  const style = initialRasterStyle(raster, overrides);
  const dem = isDem(dataset?.metadata);
  if (dem) style.product = DEM_TILE_PRODUCTS.includes(overrides.dem) ? overrides.dem : 'elevation';
  return {
    dem,
    showContours: dem && overrides.dem === 'contours',
    contours: null,
    id: `raster-${dataset.id}`,
    datasetId: dataset.id,
    name: dataset.name || dataset.id,
    visible: true,
    opacity: 0.85,
    rev: 0,
    style,
    version: raster?.job_id || '',
    bounds: rasterBounds(raster),
    raster,
  };
}

/** Tile URL of a layer (the job id busts the browser cache after a re-ingest). A DEM
 *  product other than elevation uses the server's ramp and range for that product. */
export function layerTileUrl(layer, base) {
  const product = layer.style.product;
  if (product && product !== 'elevation') return rasterTileUrl(layer.datasetId, { style: product, v: layer.version }, base);
  return rasterTileUrl(layer.datasetId, { ...layer.style, style: undefined, v: layer.version }, base);
}

/** Apply a style patch; returns a new layer with a bumped revision when the URL changes. */
export function patchLayerStyle(layer, patch) {
  const style = { ...layer.style, ...patch };
  const changed = ['colormap', 'vmin', 'vmax', 'product'].some((k) => style[k] !== layer.style[k])
    || (style.bands || []).join(',') !== (layer.style.bands || []).join(',');
  return changed ? { ...layer, style, rev: layer.rev + 1 } : layer;
}

/** Values from /raster/value as display rows. */
export function probeRows(result) {
  if (!result) return [];
  return (result.values || []).map((v) => ({ band: v.band, value: v.value }));
}

// ── Hook ────────────────────────────────────────────────────────────────────

export function useRasterLayers({ searchParams, mapRefHolder }) {
  const datasetId = searchParams.get('dataset');
  const idsKey = datasetIdsFromParams(searchParams).join(',');
  const extraKey = extraLayerIds(searchParams).join(',');
  const overrides = {
    colormap: searchParams.get('colormap') || undefined,
    vmin: searchParams.get('vmin') ?? undefined,
    vmax: searchParams.get('vmax') ?? undefined,
    dem: searchParams.get('dem') || undefined,
  };
  const overrideKey = `${overrides.colormap}|${overrides.vmin}|${overrides.vmax}|${overrides.dem}`;
  const [rasterLayers, setRasterLayers] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [probeOn, setProbeOn] = useState(false);
  const [probe, setProbe] = useState(null);
  const mounted = useRef(true);
  const overridesRef = useRef(overrides);
  overridesRef.current = overrides;
  const primaryRef = useRef(datasetId);
  primaryRef.current = datasetId;
  const loadedKeys = useRef({}); // id -> what it was loaded with, so a stacked layer is not reloaded
  const prevExtras = useRef([]);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const fitTo = useCallback((bounds) => {
    if (!bounds) return;
    let tries = 0;
    const t = setInterval(() => {
      const map = mapRefHolder?.current?.current?.getMap?.();
      if (map) {
        clearInterval(t);
        map.fitBounds(bounds, { padding: 60, duration: 600, maxZoom: 19 });
      } else if (++tries > 40) clearInterval(t);
    }, 150);
  }, [mapRefHolder]);

  const addDataset = useCallback(async (ds) => {
    // Loaded lazily so the map (and its tests) do not need the auth client until a raster is shown.
    const { supabase } = await import('../lib/supabase');
    await startTileAuth(supabase);
    if (!mounted.current) return;
    const primary = ds.id === primaryRef.current;
    const layer = buildRasterLayer(ds, primary ? overridesRef.current : {});
    setRasterLayers((prev) => [...prev.filter((l) => l.datasetId !== ds.id), layer]);
    setStatus('ready');
    if (primary) fitTo(layer.bounds);
  }, [fitTo]);

  const load = useCallback(async (id) => {
    const res = await datasetsApi.get(id);
    const ds = res.data || {};
    if (!isRasterDataset(ds)) return 'not-raster';
    const job = rasterJobState(ds.metadata);
    if (job.state === 'processing' && job.jobId) {
      setStatus('processing');
      const j = await jobsApi.get(job.jobId).then((r) => r.data).catch(() => null);
      if (j) {
        const final = await trackJob(j, { label: `Raster: ${ds.name || id}` });
        if (!mounted.current) return 'gone';
        if (final.status !== 'succeeded') {
          setError(final.error || 'Raster processing did not finish');
          setStatus('error');
          return 'failed';
        }
        return load(id);
      }
    }
    if (!hasProcessedRaster(ds.metadata)) {
      setError(job.error || 'This raster has not been processed yet; open the dataset page to process it');
      setStatus('error');
      return 'unprocessed';
    }
    await addDataset(ds);
    return 'ready';
  }, [addDataset]);

  useEffect(() => {
    // Added layers (?layers=) that left the URL drop out; the primary dataset's layer stays as before.
    const gone = prevExtras.current.filter((id) => !extraKey.split(',').includes(id));
    prevExtras.current = extraKey ? extraKey.split(',') : [];
    if (gone.length) {
      gone.forEach((id) => { delete loadedKeys.current[id]; });
      setRasterLayers((prev) => prev.filter((l) => !gone.includes(l.datasetId)));
    }
  }, [extraKey]);

  useEffect(() => {
    const ids = idsKey ? idsKey.split(',') : [];
    if (!ids.length) { setStatus('idle'); return undefined; }
    let active = true;
    setError(null);
    ids.forEach((id) => {
      const key = id === datasetId ? overrideKey : '';
      if (loadedKeys.current[id] === key) return;
      loadedKeys.current[id] = key;
      load(id)
        .then((r) => { if (active && r === 'not-raster' && id === datasetId) setStatus('idle'); })
        .catch((e) => {
          if (!active) return;
          setError(e?.response?.data?.detail || e?.message || 'Could not load this raster');
          setStatus('error');
        });
    });
    return () => { active = false; };
    // overrideKey: re-apply when the URL's colormap/range change
  }, [idsKey, datasetId, overrideKey, load]);

  const updateLayer = useCallback((id, fn) => {
    setRasterLayers((prev) => prev.map((l) => (l.id === id ? fn(l) : l)));
  }, []);
  const setLayerStyle = useCallback((id, patch) => updateLayer(id, (l) => patchLayerStyle(l, patch)), [updateLayer]);
  const setOpacity = useCallback((id, opacity) => updateLayer(id, (l) => ({ ...l, opacity })), [updateLayer]);
  const toggleLayer = useCallback((id) => updateLayer(id, (l) => ({ ...l, visible: !l.visible })), [updateLayer]);
  const removeLayer = useCallback((id) => setRasterLayers((prev) => prev.filter((l) => l.id !== id)), []);
  /** DEM contours on/off; the GeoJSON is fetched once (GET /dem/contours). */
  const setContours = useCallback((id, on) => {
    updateLayer(id, (l) => ({ ...l, showContours: on }));
    const layer = rasterLayers.find((l) => l.id === id);
    if (!on || !layer || layer.contours) return;
    terrainApi.contours(layer.datasetId)
      .then((r) => { if (mounted.current) updateLayer(id, (l) => ({ ...l, contours: r.data })); })
      .catch((e) => {
        if (!mounted.current) return;
        updateLayer(id, (l) => ({ ...l, showContours: false }));
        setError(e?.response?.data?.detail || 'Could not load the contours');
      });
  }, [rasterLayers, updateLayer]);
  // A layer opened with ?dem=contours fetches its contours once it exists.
  useEffect(() => {
    rasterLayers.filter((l) => l.showContours && !l.contours && !l.contoursRequested).forEach((l) => {
      updateLayer(l.id, (x) => ({ ...x, contoursRequested: true }));
      setContours(l.id, true);
    });
  }, [rasterLayers, updateLayer, setContours]);
  const zoomTo = useCallback((id) => {
    const l = rasterLayers.find((x) => x.id === id);
    if (l) fitTo(l.bounds);
  }, [rasterLayers, fitTo]);

  /** Map click: probe the top visible raster when the probe is on. Returns true if handled. */
  const handleMapClick = useCallback((evt) => {
    if (!probeOn || !evt?.lngLat) return false;
    const layer = [...rasterLayers].reverse().find((l) => l.visible);
    if (!layer) return false;
    const { lng, lat } = evt.lngLat;
    setProbe({ loading: true, lng, lat, layerName: layer.name });
    rasterApi.value(layer.datasetId, lng, lat)
      .then((r) => { if (mounted.current) setProbe({ lng, lat, layerName: layer.name, result: r.data }); })
      .catch((e) => {
        if (mounted.current) setProbe({ lng, lat, layerName: layer.name, error: e?.response?.data?.detail || 'Probe failed' });
      });
    return true;
  }, [probeOn, rasterLayers]);

  return {
    datasetId, rasterLayers, status, error, probeOn, setProbeOn, probe, clearProbe: () => setProbe(null),
    setLayerStyle, setOpacity, toggleLayer, removeLayer, zoomTo, handleMapClick, setContours,
  };
}
